import {
  MyLocation as MyLocationIcon,
  MapSelect as MapSelectIcon,
} from '@watersport/ui-react-icons'
import { BottomSheet, SkeletonPrimitive } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import { useLayout } from 'contexts/LayoutProvider'
import { useSearch } from 'contexts/SearchProvider'
import LocationModalHeader from 'features/common/components/LocationModalHeader'
import LocationOption from 'features/common/components/LocationOption'
import NotFoundLocation from 'features/common/components/NotFoundLocation'
import { cn } from 'features/common/helpers/cn'
import { highlightMatch } from 'features/common/helpers/utils'
import { useLocations } from 'features/common/hooks/useLocations'
import { Location, SearchScope } from 'features/common/types'

export interface LocationModalProps {
  show: boolean
  onSelect?: (location: Location) => void
  onClose?: () => void
  selectedLocation?: Location
  onClickMapView?: () => void
  scope: SearchScope | undefined
  polygons?: [number, number][][] | undefined
}

const LocationModal: React.FC<LocationModalProps> = ({
  show,
  onClose,
  onSelect,
  selectedLocation,
  onClickMapView,
  scope,
  polygons,
}) => {
  const [value, setValue] = useState<string>('')
  const [showLocationOption, setShowLocationOption] = useState<boolean>(true)
  const { isLoading, hasNextPage, loadMore, onSearch, locations, enableNearby, isNearby } =
    useLocations()
  const { myLocation, requestLocation, showModal } = useSearch()
  const { activeRefs } = useLayout()
  const prevShowRef = useRef(show)

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: false,
  })

  const onChange = useCallback(
    (v: string) => {
      setShowLocationOption(!v.length)
      setValue(v)
      onSearch(v)
    },
    [onSearch],
  )

  const onClickItem = useCallback(
    (item: Location) => {
      if (onSelect) onSelect(item)
      if (onClose) onClose()
    },
    [onClose, onSelect],
  )

  const onClear = useCallback(() => {
    setShowLocationOption(true)
    setValue('')
    onSearch('')
  }, [onSearch])

  const onClickNearby = useCallback(async () => {
    if (myLocation) {
      enableNearby(!isNearby)
    } else {
      const status = await showModal()
      if (status) {
        requestLocation()
      }
    }
  }, [enableNearby, isNearby, myLocation, requestLocation, showModal])

  const resultLabel = useMemo(() => {
    if (value) {
      return 'RESULTS'
    }
    if (isNearby) {
      return 'NEAR YOU'
    }
    return 'MOST POPULAR'
  }, [isNearby, value])

  const memoizedHeader = useMemo(
    () => (
      <LocationModalHeader
        value={value}
        onChange={onChange}
        onClear={onClear}
        onClose={() => onClose && onClose()}
      />
    ),
    [value, onChange, onClear, onClose],
  )

  const memoizedLocationOptions = useMemo(
    () => (
      <div className="px-3" data-testid="location-options">
        <LocationOption
          Icon={MyLocationIcon}
          text={isNearby ? 'Near you' : 'Search nearby'}
          onClick={onClickNearby}
          isActive={isNearby}
          dataTestId="nearby-location-option"
        />
        <LocationOption
          Icon={MapSelectIcon}
          text="Select or draw on map"
          onClick={() => onClickMapView && onClickMapView()}
          showEditButton={!selectedLocation && (!!scope || !!polygons)}
          dataTestId="map-select-location-option"
        />
      </div>
    ),
    [isNearby, onClickNearby, selectedLocation, scope, polygons, onClickMapView],
  )

  useEffect(() => {
    if (show !== prevShowRef.current) {
      if (show && selectedLocation) {
        setValue(selectedLocation.name)
        onSearch(selectedLocation.name)
      } else if (!show) {
        setValue('')
        onSearch('')
      }
      setShowLocationOption(true)
      prevShowRef.current = show
    }
  }, [show, selectedLocation, onSearch])

  useEffect(() => {
    if (locations.length === 0) {
      setShowLocationOption(true)
    }
  }, [locations])

  return (
    <BottomSheet
      show={show}
      onClose={onClose}
      title={'Select location'}
      headerClassName="p-4 pl-5"
      titleClassName="font-medium"
      closeButtonClassName="p-1 border-blue-400 text-blue-200"
      fullHeight
      activeRefs={activeRefs}
      customHeaderContent={memoizedHeader}
      data-testid="location-modal"
    >
      <div
        className="flex flex-col h-full flex-1 overflow-hidden bg-white"
        data-testid="location-modal-content"
      >
        <div
          className="flex flex-col flex-1 overflow-y-auto bg-blue-50"
          ref={rootRef}
          data-testid="location-options-container"
        >
          {showLocationOption && memoizedLocationOptions}
          <div
            className="flex justify-center items-center px-5 py-[9px] bg-blue-100"
            data-testid="result-label-container"
          >
            <h1
              className="text-sm font-medium text-blue-800 tracking-wider"
              data-testid="result-label"
            >
              {resultLabel}
            </h1>
          </div>
          {locations.length
            ? locations.map((location, index) => (
                <div
                  className="flex justify-between items-center px-5 py-3 border-b border-blue-200 leading-none"
                  key={index}
                  onClick={() => onClickItem(location)}
                  data-testid={`location-item-${index}`}
                >
                  <div>
                    <h1
                      className="text-[15px] font-semibold"
                      data-testid={`location-name-${index}`}
                    >
                      {highlightMatch(location.name, value)}
                    </h1>
                    <span
                      className="text-xs font-normal text-gray-600"
                      data-testid={`location-details-${index}`}
                    >
                      {location.city
                        ? `${location.city}, ${location.country?.name}`
                        : location.country?.name}
                    </span>
                  </div>
                  {isNearby && location.distance_km && (
                    <span
                      className={cn('text-xs text-gray-400', {
                        'text-blue-700': location.distance_km < 0.1,
                      })}
                      data-testid={`location-distance-${index}`}
                    >
                      {location.distance_km < 0.1
                        ? 'your location'
                        : `${location.distance_km <= 5 ? location.distance_km?.toFixed(1) : location.distance_km?.toFixed(0)} km`}
                    </span>
                  )}
                </div>
              ))
            : !isLoading && (
                <NotFoundLocation searchLocation={value} data-testid="not-found-location" />
              )}
          {(isLoading || hasNextPage) && (
            <div
              ref={sentryRef}
              className="px-5 py-3 space-y-2 border-b border-blue-200"
              data-testid="loading-skeleton"
            >
              <SkeletonPrimitive.Skeleton className="h-4 w-[250px] bg-blue-200 rounded-full" />
              <SkeletonPrimitive.Skeleton className="h-4 w-[200px] bg-blue-200 rounded-full" />
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}

export default LocationModal
